import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
  Link,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import { store, RootState, AppDispatch } from "./redux/store";
import "./App.css";

import {
  Button,
  ConfigProvider,
  Dropdown,
  Modal,
  Form,
  Input,
  message,
  Layout,
} from "antd";

import { AppRoutes } from "./routes/AppRoutes";
import { AdminSidebar } from "./features/AdminSidebar";
import { logout } from "./redux/slices/authSlice";
import { UserOutlined } from "@ant-design/icons";
import React from "react";
import api from "./api/axios";
import {
  NotificationsProvider,
  useNotificationsStatus,
} from "./context/NotificationsContext";
import { NotificationBell } from "./features/notifications/NotificationBell";

const { Header, Content } = Layout;

function AppContent() {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const isAuthenticated = !!token;
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isLoginPage = location.pathname === "/login";
  const isPublicLanding = location.pathname === "/" && !isAuthenticated;
  const isContactSalesPage = location.pathname === "/contact-sales";
  const isPublicPage = isLoginPage || isPublicLanding || isContactSalesPage;
  const { user } = useSelector((state: RootState) => state.auth);
  const { refreshHasNew } = useNotificationsStatus();
  const [logoFailed, setLogoFailed] = React.useState(false);
  const [iconFailed, setIconFailed] = React.useState(false);
  const [businessName, setBusinessName] = React.useState<string>("");

  const [isChangePwdOpen, setIsChangePwdOpen] = React.useState(false);
  const [isSubmittingPwd, setIsSubmittingPwd] = React.useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    if (!isAuthenticated || isPublicPage) return;
    refreshHasNew();
  }, [isAuthenticated, isPublicPage, refreshHasNew]);

  React.useEffect(() => {
    if (!isAuthenticated || isPublicPage || user?.isAdmin) return;
    let isMounted = true;

    const loadBusinessName = async () => {
      try {
        const res = await api.get("/pos/Resellers/profile");
        const data = res?.data && typeof res.data === "object" ? res.data : {};
        const name =
          data.businessName ||
          data.BusinessName ||
          data.business_name ||
          "";
        if (isMounted) {
          setBusinessName(String(name || "").trim());
        }
      } catch {
        if (isMounted) setBusinessName("");
      }
    };

    loadBusinessName();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isLoginPage, user?.isAdmin]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const openChangePassword = () => {
    form.resetFields();
    setIsChangePwdOpen(true);
  };

  const closeChangePassword = () => {
    setIsChangePwdOpen(false);
    form.resetFields();
  };

  const submitChangePassword = async () => {
    try {
      const values = await form.validateFields();
      const currentPassword = (values.currentPassword || "").trim();
      const newPassword = (values.newPassword || "").trim();
      const confirmNewPassword = (values.confirmNewPassword || "").trim();

      if (!currentPassword || !newPassword || !confirmNewPassword) {
        message.error("All password fields are required");
        return;
      }
      if (newPassword !== confirmNewPassword) {
        message.error("New passwords do not match");
        return;
      }
      if (newPassword.length < 8) {
        message.error("New password must be at least 8 characters");
        return;
      }

      const authToken = localStorage.getItem("token");
      if (!authToken) {
        message.error("Not authenticated. Please login again.");
        handleLogout();
        return;
      }

      setIsSubmittingPwd(true);

      // ✅ FIX: same-origin API call, no hardcoded IP, no mixed content
      const url = "/api/User/change-password";

      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          confirmNewPassword,
        }),
      });

      const text = await res.text();
      let data: any = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          `Change password failed (${res.status})`;
        message.error(msg);
        return;
      }

      message.success("Password updated successfully");
      closeChangePassword();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmittingPwd(false);
    }
  };

  if (!isAuthenticated && !isPublicPage) {
    window.location.href = "/login";
    return null;
  }

  if (isPublicPage) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content>
          <AppRoutes />
        </Content>
      </Layout>
    );
  }

  const accountMenuItems = [
    {
      key: "notifications",
      label: "Notifications",
      onClick: () =>
        navigate(
          user?.isAdmin
            ? "/admin/notifications"
            : "/account/notifications"
        ),
    },
    ...(!user?.isAdmin
      ? [
          {
            key: "business-info",
            label: "Business Info",
            onClick: () => navigate("/account/business-info"),
          },
        ]
      : []),
    {
      key: "change-password",
      label: "Change Password",
      onClick: openChangePassword,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header className="topbar">
        <Link to="/" className="topbar__brand">
          {!logoFailed ? (
            <img
              src="/teltrip-logo.png"
              alt="Teltrip"
              className="brand-logo"
              onError={() => setLogoFailed(true)}
            />
          ) : !iconFailed ? (
            <img
              src="/teltrip-icon.png"
              alt="Teltrip"
              className="brand-icon"
              onError={() => setIconFailed(true)}
            />
          ) : (
            <div className="brand-mark">TT</div>
          )}
        </Link>

        <div className="topbar__center">
          <span className="topbar__center-title">Resellers Program</span>
          <span className="topbar__center-divider">|</span>
          <span className="topbar__center-subtitle">
            {businessName || user?.username || "Your Business"}
          </span>
        </div>

        <div className="topbar__actions">
          <NotificationBell />
          <Dropdown
            menu={{ items: accountMenuItems as any }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button type="primary" size="small" icon={<UserOutlined />}>
              Account
            </Button>
          </Dropdown>
        </div>
      </Header>

      <Layout className="app-shell">
        {user?.isAdmin && <AdminSidebar />}
        <Layout className="app-content">
          <Content className="app-content__inner">
            <AppRoutes />
          </Content>
        </Layout>
      </Layout>

      <Modal
        title="Change password"
        open={isChangePwdOpen}
        onCancel={closeChangePassword}
        onOk={submitChangePassword}
        okText="Update"
        confirmLoading={isSubmittingPwd}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Current password"
            name="currentPassword"
            rules={[{ required: true, message: "Current password is required" }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            label="New password"
            name="newPassword"
            rules={[
              { required: true, message: "New password is required" },
              { min: 8, message: "Minimum 8 characters" },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            label="Confirm new password"
            name="confirmNewPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "Please confirm your new password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Passwords do not match")
                  );
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <ConfigProvider>
        <NotificationsProvider>
          <Router>
            <AppContent />
          </Router>
        </NotificationsProvider>
      </ConfigProvider>
    </Provider>
  );
}
